import { useEffect } from "react";
import { useLanguage } from "@/components/LanguageContext";

interface SEOProps {
  title?: string;
  description?: string;
  type?: string;
  image?: string;
  canonical?: string;
}

export function SEO({ 
  title, 
  description, 
  type = "website", 
  image = "/images/og-image.jpg",
  canonical 
}: SEOProps) {
  const { language } = useLanguage();
  
  const siteName = "AVISIUM Solutions Group";
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const baseUrl = "https://avisium.ma";

  useEffect(() => {
    // 1. Update Document Title
    document.title = fullTitle;

    // 2. Update Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description || "");

    // 3. Update Open Graph Tags
    const ogTags = {
      'og:title': fullTitle,
      'og:description': description || "",
      'og:type': type,
      'og:url': window.location.href,
      'og:image': `${baseUrl}${image}`,
      'og:site_name': siteName,
      'og:locale': language === 'fr' ? 'fr_FR' : 'en_US',
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    });

    // 4. Update Twitter Tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': fullTitle,
      'twitter:description': description || "",
      'twitter:image': `${baseUrl}${image}`,
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    });

    // 5. Canonical Link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical || window.location.href);

  }, [fullTitle, description, type, image, canonical, language]);

  return null;
}
